import { css } from '@emotion/css';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState } from '../../features';
import { actions, ChatErrorPayload, ChatErrorType } from '../../features/chat';
import { createInvalidCharText } from '../../lib/message-validator';
import { getQueryParam, throttle } from '../../lib/utils';
import { ConfirmButton } from '../shared/buttons/ConfirmButton';
import { Dialog } from '../shared/dialog/Dialog';

export const ChatErrorDialog: React.FC = () => {
  const { t } = useTranslation(['chat', 'translation']);
  const location = useLocation();
  const history = useHistory();
  const { errorPayload } = useSelector((state: RootState) => state.chat);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(actions.clearError());
  }, [dispatch]);

  const handleCloseClick = useCallback(
    throttle(() => {
      history.goBack();
    }, 1000),
    [history]
  );

  const typesParam = getQueryParam(location.search || '', 'types');
  const errorTypes = (typesParam ? typesParam.split(',') : []) as ChatErrorType[];
  const text = getTextErrorDialog(t, errorTypes, errorPayload);

  return (
    <Dialog title={t('error_send_failed')}>
      <>
        <p
          className={css`
            flex: 1 1 auto;
            display: flex;
            padding: 12px 0 32px;
            opacity: ${errorTypes.length > 0 ? '1' : '0'};
          `}
        >
          {text}
        </p>

        <div
          className={css`
            flex: 0 0 auto;
            padding: 0 0 4px;
          `}
        >
          <ConfirmButton onClick={handleCloseClick}>
            {t('translation:dialog_button_ok')}
          </ConfirmButton>
        </div>
      </>
    </Dialog>
  );
};

/* Helper function */

function getTextErrorDialog(
  t: TFunction,
  errorTypes: ChatErrorType[],
  errorPayload?: ChatErrorPayload
): string {
  const hasTooLong = errorTypes.find(et => et === 'text-is-too-long');
  const hasInvalidChar = errorTypes.find(et => et === 'invalid-char');

  let chars: string | undefined;
  if (errorPayload && errorPayload.invalidChars) {
    chars = createInvalidCharText(errorPayload.invalidChars);
  }
  if (hasTooLong && hasInvalidChar) {
    return t('error_invalid_message', { chars });
  }
  if (hasTooLong) {
    return t('error_too_long');
  }
  if (hasInvalidChar) {
    return t('error_invalid_char', { chars });
  }

  if (errorTypes.find(et => et === 'im-offline')) {
    return t('error_im_offline');
  }
  if (errorTypes.find(et => et === 'communication-disabled')) {
    return t('translation:error_communication_off');
  }
  if (errorTypes.find(et => et === 'unstable-network')) {
    return t('translation:error_unstable_network');
  }
  if (errorTypes.find(et => et === 'dest-offline')) {
    return t('error_dest_offline');
  }
  if (errorTypes.find(et => et === 'rate-limit-exceeded')) {
    return t('translation:error_rate_limit_exceeded');
  }
  return t('translation:error_unknown');
}
